import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  getTeacherClasses,
  createTeacherClass,
  updateTeacherClass,
  deleteTeacherClass,
  generateCode
} from '../services/teacherClassService'
import { getEstablishments } from '../services/referenceDataService'
import { deleteAssignmentsForTeacherClass } from '../services/assignmentService'
import { useBackdropDismiss } from '../hooks/useBackdropDismiss'
import './TeacherClasses.css'

export default function TeacherClasses() {
  const { user, userData } = useAuth()
  const navigate = useNavigate()
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createCode, setCreateCode] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [createEstablishment, setCreateEstablishment] = useState('')
  const [establishmentsList, setEstablishmentsList] = useState([])
  const [deletingId, setDeletingId] = useState(null)
  const [copiedId, setCopiedId] = useState(null)
  const [editClass, setEditClass] = useState(null)
  const [editName, setEditName] = useState('')
  const [editCode, setEditCode] = useState('')
  const [editEstablishment, setEditEstablishment] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')

  const loadClasses = useCallback(() => {
    if (!user) return
    setLoading(true)
    getTeacherClasses(user.uid)
      .then(setClasses)
      .catch(() => setClasses([]))
      .finally(() => setLoading(false))
  }, [user])

  useEffect(() => {
    loadClasses()
  }, [loadClasses])

  useEffect(() => {
    getEstablishments()
      .then((list) => setEstablishmentsList((list || []).filter((n) => n && n !== 'Autre')))
      .catch(() => setEstablishmentsList([]))
  }, [])

  useEffect(() => {
    if (!user || userData?.role !== 'teacher') {
      navigate('/dashboard')
      return
    }
  }, [user, userData?.role, navigate])

  const handleCreate = async (e) => {
    e.preventDefault()
    setCreateError('')
    const name = createName.trim()
    if (!name) {
      setCreateError('Veuillez saisir un nom pour la classe.')
      return
    }
    setCreating(true)
    try {
      await createTeacherClass(
        user.uid,
        name,
        createCode.trim() || null,
        createEstablishment.trim() || null
      )
      setCreateName('')
      setCreateCode('')
      setCreateEstablishment('')
      setShowCreate(false)
      loadClasses()
    } catch (err) {
      setCreateError(err.message || 'Erreur lors de la création.')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (classId) => {
    if (
      !window.confirm(
        'Supprimer cette classe ? Tous les devoirs assignés à cette classe seront supprimés. Les élèves resteront dans votre liste « Mes élèves » (ils peuvent avoir à rejoindre une autre classe).'
      )
    ) {
      return
    }
    setDeletingId(classId)
    try {
      await deleteTeacherClass(classId, user.uid)
      loadClasses()
    } catch (err) {
      alert(err.message || 'Erreur lors de la suppression.')
    } finally {
      setDeletingId(null)
    }
  }

  const handleClearAssignments = async (classId, className) => {
    const label = className || 'cette classe'
    if (
      !window.confirm(
        `Supprimer tous les devoirs de « ${label} » ? Les tentatives des élèves peuvent rester en base sans lien vers le devoir.`
      )
    ) {
      return
    }
    setDeletingId(classId)
    try {
      const n = await deleteAssignmentsForTeacherClass(user.uid, classId)
      alert(n > 0 ? `${n} devoir${n > 1 ? 's' : ''} supprimé${n > 1 ? 's' : ''}.` : 'Aucun devoir à supprimer.')
    } catch (err) {
      alert(err.message || 'Erreur lors de la suppression des devoirs.')
    } finally {
      setDeletingId(null)
    }
  }

  const copyCode = (classId, code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedId(classId)
      setTimeout(() => setCopiedId(null), 2000)
    })
  }

  const dismissCreate = useCallback(() => {
    if (!creating) setShowCreate(false)
  }, [creating])
  const dismissEdit = useCallback(() => {
    if (!editSaving) setEditClass(null)
  }, [editSaving])
  const createBackdropDismiss = useBackdropDismiss(dismissCreate)
  const editBackdropDismiss = useBackdropDismiss(dismissEdit)

  if (!user || userData?.role !== 'teacher') return null

  return (
    <div className="teacher-classes-page">
      <header className="teacher-classes-header">
        <button type="button" className="teacher-classes-back" onClick={() => navigate('/dashboard')}>
          ← Retour au tableau de bord
        </button>
        <h1 className="teacher-classes-title">Mes classes</h1>
        <p className="teacher-classes-intro">
          Créez des classes (avec l’établissement associé) et partagez le code : au rejoindre, le profil élève
          reprend automatiquement cet établissement et le nom de la classe.
        </p>
      </header>

      <div className="teacher-classes-actions">
        <button
          type="button"
          className="teacher-classes-btn-create"
          onClick={() => {
            setShowCreate(true)
            setCreateError('')
            setCreateName('')
            setCreateCode('')
            setCreateEstablishment('')
          }}
        >
          Créer une classe
        </button>
      </div>

      {showCreate && (
        <div className="teacher-classes-create-backdrop" {...createBackdropDismiss}>
          <div className="teacher-classes-create-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Nouvelle classe</h2>
            <form onSubmit={handleCreate}>
              <label>
                Nom de la classe
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="ex. 1ère S – Groupe A"
                  autoFocus
                />
              </label>
              <label>
                Établissement
                <select
                  className="teacher-classes-create-select"
                  value={createEstablishment}
                  onChange={(e) => setCreateEstablishment(e.target.value)}
                >
                  <option value="">— Non renseigné —</option>
                  {establishmentsList.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <span className="teacher-classes-field-hint">
                  Recommandé : les élèves qui rejoignent avec le code héritent de cet établissement sur leur profil.
                </span>
              </label>
              <label>
                Code (optionnel)
                <input
                  type="text"
                  value={createCode}
                  onChange={(e) => setCreateCode(e.target.value.toUpperCase())}
                  placeholder="Laissé vide = généré automatiquement"
                  maxLength={20}
                />
              </label>
              {createError && <p className="teacher-classes-create-error">{createError}</p>}
              <div className="teacher-classes-create-actions">
                <button type="button" onClick={() => !creating && setShowCreate(false)} disabled={creating}>
                  Annuler
                </button>
                <button type="submit" disabled={creating}>
                  {creating ? 'Création…' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editClass && (
        <div className="teacher-classes-create-backdrop" {...editBackdropDismiss}>
          <div className="teacher-classes-create-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Modifier la classe</h2>
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                setEditError('')
                const name = editName.trim()
                if (!name) {
                  setEditError('Veuillez saisir un nom pour la classe.')
                  return
                }
                const code = editCode.trim().toUpperCase()
                if (!code) {
                  setEditError('Veuillez saisir un code pour la classe.')
                  return
                }
                setEditSaving(true)
                try {
                  await updateTeacherClass(editClass.id, user.uid, {
                    name,
                    code,
                    establishment: editEstablishment.trim() || null
                  })
                  setEditClass(null)
                  loadClasses()
                } catch (err) {
                  setEditError(err.message || 'Erreur lors de la mise à jour.')
                } finally {
                  setEditSaving(false)
                }
              }}
            >
              <label>
                Nom de la classe
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  autoFocus
                  disabled={editSaving}
                />
              </label>
              <label>
                Code pour rejoindre
                <div className="teacher-classes-edit-code-row">
                  <input
                    type="text"
                    value={editCode}
                    onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                    maxLength={20}
                    disabled={editSaving}
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="teacher-classes-regenerate-btn"
                    disabled={editSaving}
                    onClick={() => setEditCode(generateCode())}
                    title="Générer un nouveau code aléatoire"
                  >
                    Régénérer
                  </button>
                </div>
                <span className="teacher-classes-field-hint">
                  Changer le code invalide l’ancien : les élèves déjà dans la classe restent inscrits ; les nouveaux devront utiliser le nouveau code.
                </span>
              </label>
              <label>
                Établissement
                <select
                  className="teacher-classes-create-select"
                  value={editEstablishment || ''}
                  onChange={(e) => setEditEstablishment(e.target.value)}
                  disabled={editSaving}
                >
                  <option value="">— Non renseigné —</option>
                  {editEstablishment &&
                    !establishmentsList.includes(editEstablishment) &&
                    editEstablishment !== '' && (
                      <option value={editEstablishment}>{editEstablishment}</option>
                    )}
                  {establishmentsList.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              {editError && <p className="teacher-classes-create-error">{editError}</p>}
              <div className="teacher-classes-create-actions">
                <button type="button" onClick={() => !editSaving && setEditClass(null)} disabled={editSaving}>
                  Annuler
                </button>
                <button type="submit" disabled={editSaving}>
                  {editSaving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="teacher-classes-loading">
          <div className="spinner" />
          <p>Chargement des classes…</p>
        </div>
      ) : classes.length === 0 ? (
        <div className="teacher-classes-empty">
          <p>Aucune classe pour le moment.</p>
          <p>Créez une classe et communiquez le code à vos élèves pour qu’ils vous rejoignent depuis leur profil.</p>
        </div>
      ) : (
        <ul className="teacher-classes-list">
          {classes.map((c) => (
            <li key={c.id} className="teacher-classes-card">
              <div className="teacher-classes-card-main">
                <h3 className="teacher-classes-card-name">{c.name}</h3>
                {c.establishment ? (
                  <p className="teacher-classes-card-establishment">{c.establishment}</p>
                ) : null}
                <div className="teacher-classes-card-code-row">
                  <code className="teacher-classes-card-code">{c.code}</code>
                  <button
                    type="button"
                    className="teacher-classes-copy-btn"
                    onClick={() => copyCode(c.id, c.code)}
                    title="Copier le code"
                  >
                    {copiedId === c.id ? 'Copié !' : 'Copier'}
                  </button>
                </div>
              </div>
              <div className="teacher-classes-card-actions">
                <button
                  type="button"
                  className="teacher-classes-card-edit"
                  onClick={() => {
                    setEditClass(c)
                    setEditName(c.name || '')
                    setEditCode(c.code || '')
                    setEditEstablishment(c.establishment || '')
                    setEditError('')
                  }}
                >
                  Modifier
                </button>
                <button
                  type="button"
                  className="teacher-classes-card-link"
                  onClick={() => navigate('/dashboard/students', { state: { filterMyStudents: true } })}
                >
                  Voir mes élèves
                </button>
                <button
                  type="button"
                  className="teacher-classes-card-clear-assignments"
                  onClick={() => handleClearAssignments(c.id, c.name)}
                  disabled={deletingId === c.id}
                  title="Supprimer tous les devoirs de cette classe sans supprimer la classe"
                >
                  Vider les devoirs
                </button>
                <button
                  type="button"
                  className="teacher-classes-card-delete"
                  onClick={() => handleDelete(c.id)}
                  disabled={deletingId === c.id}
                  title="Supprimer la classe"
                >
                  {deletingId === c.id ? '…' : 'Supprimer'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
